'use client'

import {useCallback, useEffect, useRef} from 'react'

const BUILD_VERSION_STORAGE_KEY = 'fitro-client-build-version'
const BUILD_VERSION_URL = '/api/build-version'
const CHECK_INTERVAL_MS = 5 * 60 * 1000

type BuildVersionResponse = {
    version?: unknown
}

function getVersion(payload: BuildVersionResponse): string | null {
    return typeof payload.version === 'string' && payload.version.trim() ? payload.version.trim() : null
}

export function shouldReloadForBuildVersion(previousVersion: string | null, currentVersion: string): boolean {
    return Boolean(previousVersion && previousVersion !== currentVersion)
}

export function BuildVersionGuard() {
    const inFlightRef = useRef(false)

    const checkBuildVersion = useCallback(async () => {
        if (inFlightRef.current) return
        inFlightRef.current = true

        try {
            const response = await fetch(BUILD_VERSION_URL, {
                cache: 'no-store',
                credentials: 'same-origin',
            })
            if (!response.ok) return

            const version = getVersion(await response.json() as BuildVersionResponse)
            if (!version || version === 'unknown') return

            const previousVersion = window.sessionStorage.getItem(BUILD_VERSION_STORAGE_KEY)
            window.sessionStorage.setItem(BUILD_VERSION_STORAGE_KEY, version)

            if (shouldReloadForBuildVersion(previousVersion, version)) {
                window.location.reload()
            }
        } catch {
            // Best-effort only. Normal navigation must not depend on this guard.
        } finally {
            inFlightRef.current = false
        }
    }, [])

    useEffect(() => {
        const initialCheck = window.setTimeout(() => {
            void checkBuildVersion()
        }, 0)
        const interval = window.setInterval(() => {
            void checkBuildVersion()
        }, CHECK_INTERVAL_MS)
        const handleFocus = () => {
            void checkBuildVersion()
        }
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                void checkBuildVersion()
            }
        }

        window.addEventListener('focus', handleFocus)
        document.addEventListener('visibilitychange', handleVisibility)

        return () => {
            window.clearTimeout(initialCheck)
            window.clearInterval(interval)
            window.removeEventListener('focus', handleFocus)
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [checkBuildVersion])

    return null
}
